if (!customElements.get('animal-calendar')) {
  customElements.define('animal-calendar', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const slides = this.querySelector('.ac__slides');
      const thumbs = [...this.querySelectorAll('[data-thumb]')];
      let index = 0;
      const go = (next) => {
        index = (next + thumbs.length) % thumbs.length;
        slides.scrollTo({ left: index * slides.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      };
      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => go(i), options));
      this.querySelectorAll('[data-direction]').forEach(button => button.addEventListener('click', () => go(index + Number(button.dataset.direction)), options));
      slides.addEventListener('scroll', () => {
        index = Math.round(slides.scrollLeft / slides.clientWidth);
        thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
        this.querySelector('[data-photo-count]').textContent = `${index + 1} / ${thumbs.length}`;
      }, { ...options, passive: true });
      slides.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          go(index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, options);
      const dialog = this.querySelector('dialog');
      this.querySelectorAll('[data-slide]').forEach(button => button.addEventListener('click', () => {
        const img = button.querySelector('img');
        if (!img) return;
        const enlarged = dialog.querySelector('img');
        enlarged.src = img.src;
        enlarged.alt = img.alt;
        dialog.showModal();
      }, options));
      dialog.querySelector('button').addEventListener('click', () => dialog.close(), options);
      dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); }, options);
      const form = this.querySelector('form');
      const quantity = form.querySelector('[name="quantity"]');
      const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(cents / 100);
      const updateTotal = () => {
        const pack = form.querySelector('[data-pack]:checked');
        quantity.value = pack.value;
        const total = Number(pack.dataset.total) + [...form.querySelectorAll('[data-service]:checked')].reduce((sum, item) => sum + Number(item.dataset.price), 0);
        this.querySelector('[data-selection-total]').textContent = money(total);
        this.querySelector('.ac__sticky-product span').textContent = money(total);
        const stickyBuy = this.querySelector('.ac__sticky button');
        if (!stickyBuy.disabled) stickyBuy.textContent = `ADD TO CART · ${money(total)}`;
      };
      form.addEventListener('change', updateTotal, options);
      updateTotal();
      const openCart = async (trigger) => {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer?.renderContents) throw new Error('Please refresh the page to load your cart. Your added items are saved.');
        const url = new URL(window.Shopify?.routes?.root || '/', window.location.origin);
        const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
        const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('Unable to open your cart. Please try again. Your added items are saved.');
        const sections = await response.json();
        if (!sections['cart-drawer']) {
          url.searchParams.delete('sections');
          url.searchParams.set('section_id', 'cart-drawer');
          const fallback = await fetch(url, { cache: 'no-store' });
          if (fallback.ok) sections['cart-drawer'] = await fallback.text();
        }
        const parsed = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
        if (!parsed.querySelector('#CartDrawer')) throw new Error('Your items are saved, but the cart could not load. Please try VIEW CART again.');
        if (!sections['cart-icon-bubble']) {
          const icon = document.getElementById('cart-icon-bubble');
          sections['cart-icon-bubble'] = `<div class="shopify-section">${icon?.innerHTML || ''}</div>`;
        }
        drawer.setActiveElement(trigger);
        drawer.renderContents({ sections });
      };
      this.querySelectorAll('[data-quantity]').forEach(button => button.addEventListener('click', () => {
        quantity.value = Math.max(1, (parseInt(quantity.value, 10) || 1) + Number(button.dataset.quantity));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }, options));
      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (this.submitting || !form.reportValidity()) return;
        this.submitting = true;
        const buttons = [...this.querySelectorAll('button[type="submit"]')];
        const label = this.querySelector('[data-buy-label]');
        const error = this.querySelector('.ac__error');
        buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
        label.textContent = 'ADDING…';
        error.hidden = true;
        try {
          if (this.added) {
            await openCart(event.submitter);
            this.added = false;
            return;
          }
          const beforeResponse = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } });
          if (!beforeResponse.ok) throw new Error('Unable to check your cart. Please try again.');
          const before = await beforeResponse.json();
          const variantId = Number(form.querySelector('[name="id"]').value);
          const items = [{ id: variantId, quantity: Number(quantity.value) }];
          const selectedServices = [...form.querySelectorAll('[data-service]:checked')];
          selectedServices.forEach(service => {
            if (!before.items.some(item => item.variant_id === Number(service.value))) items.push({ id: Number(service.value), quantity: 1 });
          });
          const response = await fetch(this.dataset.addUrl, { method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || 'We could not add this item. Please try again.');
          this.added = true;
          const count = before.items.filter(item => item.variant_id === variantId).reduce((sum, item) => sum + item.quantity, 0) + Number(quantity.value);
          const code = this.dataset.currency === 'USD' ? (count >= 10 ? 'MLCAL10' : count >= 3 ? 'MLCAL3' : count >= 2 ? 'MLCAL2' : '') : '';
          if (!window.MirthloomCalendarCart) throw new Error('Items were added. Select VIEW CART to verify your order before checkout.');
          const updated = await window.MirthloomCalendarCart.normalize();
          const expectedSavings = count >= 10 ? 4991 : count >= 3 ? 998 : count >= 2 ? 499 : 0;
          const bundleApplied = (updated.discount_codes || []).some(item => item.code.toUpperCase() === code && item.applicable);
          if (code && !bundleApplied && Number(updated.total_discount) < expectedSavings) throw new Error('Items were added. The bundle offer may conflict with another discount. Select VIEW CART to check your final price before checkout.');
          await openCart(event.submitter);
          this.added = false;
        } catch (err) {
          error.textContent = err.message || 'Something went wrong. Please try again.';
          error.hidden = false;
          error.scrollIntoView({ block: 'center', behavior: 'smooth' });
        } finally {
          this.submitting = false;
          buttons.forEach(button => { button.disabled = false; button.removeAttribute('aria-busy'); });
          label.textContent = this.added ? 'VIEW CART' : 'ADD TO CART';
          if (this.added) this.querySelector('.ac__sticky button').textContent = 'VIEW CART';
          else updateTotal();
        }
      }, options);
      const sticky = this.querySelector('.ac__sticky');
      const main = this.querySelector('[data-main-buy]');
      this.observer = new IntersectionObserver(([entry]) => {
        sticky.hidden = entry.isIntersecting || entry.boundingClientRect.top > 0;
      }, { threshold: 0 });
      this.observer.observe(main);
    }
    disconnectedCallback() { this.controller?.abort(); this.observer?.disconnect(); this.initialized = false; }
  });
}
